import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { usePullToRefresh } from '../hooks/usePullToRefresh';

/**
 * Global pull-to-refresh (#2089). The #2058 gesture hook + drag indicator
 * lived inside Today.tsx only; every other page silently lacked the gesture.
 * This provider mounts ONE gesture listener + ONE indicator at the app shell
 * and lets the current page register what "refresh" means for it via
 * usePageRefresh(handler).
 *
 * Pages that don't register get an inert gesture (enabled: false) — that's
 * the opt-out for surfaces where a swipe must not hijack touch handling or
 * destroy in-progress work (signature canvas, barcode scanner viewport,
 * mid-task StoreDetail, order entry).
 */

type RefreshHandler = () => Promise<void> | void;

interface PullToRefreshContextValue {
  /** Register the page's refresh action; returns an unregister cleanup. */
  register: (fn: RefreshHandler) => () => void;
}

const PullToRefreshContext = createContext<PullToRefreshContextValue>({
  register: () => () => {},
});

export function PullToRefreshProvider({ children }: { children: ReactNode }) {
  const [handler, setHandler] = useState<RefreshHandler | null>(null);

  const register = useCallback((fn: RefreshHandler) => {
    setHandler(() => fn);
    return () => {
      // Only clear if this registration is still the active one (a new page
      // may have registered before the old page's cleanup ran).
      setHandler((current) => (current === fn ? null : current));
    };
  }, []);

  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  const { pullDistance, isRefreshing } = usePullToRefresh(
    async () => {
      if (handlerRef.current) await handlerRef.current();
    },
    { enabled: !!handler }
  );

  return (
    <PullToRefreshContext.Provider value={{ register }}>
      {/* Drag indicator (lifted verbatim from Today's #2058 implementation) */}
      {(pullDistance > 0 || isRefreshing) && (
        <div
          data-testid="ptr-indicator"
          className="pointer-events-none fixed inset-x-0 top-0 z-50 flex justify-center"
          style={{ transform: `translateY(${Math.max(0, (isRefreshing ? 24 : pullDistance) - 12)}px)` }}
        >
          <div className="mt-1 rounded-full bg-white p-2 shadow-lg">
            <svg
              className={`h-5 w-5 text-primary-600 ${isRefreshing ? 'animate-spin' : ''}`}
              style={isRefreshing ? undefined : { transform: `rotate(${pullDistance * 3}deg)` }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </div>
        </div>
      )}
      {children}
    </PullToRefreshContext.Provider>
  );
}

/**
 * Register this page's pull-to-refresh action for as long as it's mounted.
 * The latest `fn` is always called (no stale closures), and the registration
 * is stable across re-renders.
 */
export function usePageRefresh(fn: RefreshHandler): void {
  const { register } = useContext(PullToRefreshContext);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  useEffect(() => register(() => fnRef.current()), [register]);
}
